import { normalizeItems, ChecklistItem } from '../../../store/checklist-item-helpers'

export const DEFAULT_TAGS = ['feedback', 'ideas', 'announcements', 'marketplace', 'questions', 'team-updates', 'fun', 'wellness']
const TAG_RE = /^[\p{L}\p{M}\p{N}_-]+$/u
const MAX_TAGS = 10
const MAX_TAG_LEN = 50

export function normalizeTag(raw: string): string {
  return String(raw || '').trim().replace(/^#+/, '').toLowerCase().slice(0, MAX_TAG_LEN)
}
export function isValidTag(raw: string): boolean {
  const t = normalizeTag(raw)
  return t.length > 0 && TAG_RE.test(t)
}
export function addTag(tags: string[], raw: string, max: number = MAX_TAGS): string[] {
  const t = normalizeTag(raw)
  if (!t || !TAG_RE.test(t) || tags.includes(t) || tags.length >= max) return tags
  return [...tags, t]
}

export type PostStatus = 'draft' | 'scheduled' | 'published'
export function postStatus(post: any): PostStatus {
  if (post && post.status === 'draft') return 'draft'
  if (post && post.status === 'scheduled') return 'scheduled'
  return 'published'
}

export function isPinned(post: any): boolean {
  return !!(post && (post.pined === true || post.pinned === true))
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// The store stores two date fields: `create_date` ("YYYY-MM-DD HH:mm:ss",
// sortable) and `createdate` ("MMM D, YYYY [at] H:mm A", human display).
// Always derive logic from the sortable one; fall back defensively.
export function postDateKey(post: any): string {
  const iso = post && post.create_date
  if (typeof iso === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
    if (m) return m[1]
  }
  const human = post && post.createdate
  if (typeof human === 'string') {
    const m2 = /^(\d{4}-\d{2}-\d{2})/.exec(human)
    if (m2) return m2[1]
    const d = new Date(human)
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    }
  }
  return ''
}

function ymd(s: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '')
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null
}

// Gmail-style relative day buckets used as list section headers.
export function dateBucket(dateKey: string, todayStr: string): string {
  const a = ymd(dateKey), b = ymd(todayStr)
  if (!a || !b) return 'Earlier'
  const diff = Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86400000)
  if (diff <= 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  if (diff <= 6) return 'This week'
  if (a[0] === b[0] && a[1] === b[1]) return 'This month'
  return `${MONTHS[a[1] - 1]} ${a[0]}`
}

// Compact per-row timestamp: time-of-day for today, else a short date
// (year dropped only when it's the current year).
export function rowTime(post: any, todayStr: string): string {
  const human = post && post.createdate
  const key = postDateKey(post)
  if (typeof human === 'string') {
    const idx = human.indexOf(' at ')
    if (idx !== -1) {
      const datePart = human.slice(0, idx).trim()
      const timePart = human.slice(idx + 4).trim()
      if (key && key === todayStr) return timePart
      const yr = (todayStr || '').slice(0, 4)
      return datePart.replace(new RegExp(',\\s*' + yr + '$'), '').trim()
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(human)) return key === todayStr ? human.slice(11, 16) : human.slice(5, 10)
  }
  const iso = post && post.create_date
  if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso)) return key === todayStr ? iso.slice(11, 16) : iso.slice(5, 10)
  return ''
}

// "YYYY-MM-DD HH:mm:ss" -> "Jun 12, 06:54" for comment timestamps.
export function formatStamp(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(s || ''))
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[4]}:${m[5]}` : String(s || '')
}

export interface InboxGroups { pinned: any[]; scheduled: any[]; today: any[]; earlier: any[] }
export function groupPostsForInbox(posts: any[], todayStr: string): InboxGroups {
  const pinned: any[] = [], scheduled: any[] = [], today: any[] = [], earlier: any[] = []
  for (const p of posts || []) {
    const st = postStatus(p)
    if (st === 'scheduled') { scheduled.push(p); continue }
    if (st === 'draft') { earlier.push(p); continue }
    if (isPinned(p)) { pinned.push(p); continue }
    if (postDateKey(p) === todayStr) today.push(p)
    else earlier.push(p)
  }
  return { pinned, scheduled, today, earlier }
}

export function filterPostsByTag(posts: any[], tag: string): any[] {
  if (!tag) return posts || []
  const t = normalizeTag(tag)
  return (posts || []).filter(p => Array.isArray(p.tags) && p.tags.includes(t))
}

export interface TrendingTag { name: string; count: number }
export function topTags(tagsDoc: any, limit: number = 20): TrendingTag[] {
  const tags = (tagsDoc && tagsDoc.tags) || {}
  return Object.keys(tags)
    .map(name => ({ name, count: Number((tags[name] && tags[name].count) || 0) }))
    .filter(t => t.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, limit)
}
export function mergeTagSuggestions(tagsDoc: any, defaults: string[] = DEFAULT_TAGS, limit: number = 6): TrendingTag[] {
  const company = topTags(tagsDoc, 1000)
  const seen = new Set(company.map(t => t.name))
  const merged = [...company]
  for (const d of defaults) if (!seen.has(d)) merged.push({ name: d, count: 0 })
  return merged.slice(0, limit)
}

export interface ComposeOpts { official: boolean; requireAck: boolean; notify: 'push' | 'quiet'; publishAt: string | null }
export function buildPostPayload(form: { title: string; description: string; tags: string[] }, opts: ComposeOpts): any {
  const payload: any = {
    title: (form.title || '').trim(),
    description: form.description || '',
    official: !!opts.official,
    require_ack: !!opts.requireAck,
    notify: opts.notify === 'push' ? 'push' : 'quiet',
    status: opts.publishAt ? 'scheduled' : 'published',
  }
  if (opts.publishAt) payload.publish_at = opts.publishAt
  if (Array.isArray(form.tags) && form.tags.length) payload.tags = form.tags.slice(0, MAX_TAGS)
  return payload
}

export interface SectionGroup { label: string; items: any[] }
export interface SectionOpts { section: string; tag: string; query: string; todayStr: string }
export function buildSectionGroups(posts: any[], opts: SectionOpts): SectionGroup[] {
  let list = filterPostsByTag(posts || [], opts.tag)
  const q = (opts.query || '').trim().toLowerCase()
  if (q) list = list.filter(p => ((p.title || '').toLowerCase().includes(q)) || ((p.create_name || '').toLowerCase().includes(q)))

  if (opts.section === 'pinned') return [{ label: 'Pinned', items: list.filter(p => isPinned(p) && postStatus(p) === 'published') }].filter(s => s.items.length > 0)
  if (opts.section === 'scheduled') return [{ label: 'Scheduled', items: list.filter(p => postStatus(p) === 'scheduled') }].filter(s => s.items.length > 0)
  if (opts.section === 'drafts') return [{ label: 'Drafts', items: list.filter(p => postStatus(p) === 'draft') }].filter(s => s.items.length > 0)

  // Default: pinned and scheduled float to the top, everything else is
  // grouped into relative-date buckets. Input is pre-sorted desc by
  // create_date, so first-seen bucket order is already chronological.
  const pinned: any[] = [], scheduled: any[] = []
  const order: string[] = []
  const buckets: { [k: string]: any[] } = {}
  for (const p of list) {
    const st = postStatus(p)
    if (st === 'scheduled') { scheduled.push(p); continue }
    if (st === 'draft') continue
    if (isPinned(p)) { pinned.push(p); continue }
    const label = dateBucket(postDateKey(p), opts.todayStr)
    if (!buckets[label]) { buckets[label] = []; order.push(label) }
    buckets[label].push(p)
  }
  const groups: SectionGroup[] = []
  if (pinned.length) groups.push({ label: 'Pinned', items: pinned })
  if (scheduled.length) groups.push({ label: 'Scheduled', items: scheduled })
  for (const l of order) groups.push({ label: l, items: buckets[l] })
  return groups
}

export function countGroupItems(groups: SectionGroup[]): number {
  return (groups || []).reduce((n, g) => n + ((g.items && g.items.length) || 0), 0)
}

// Incrementally render rows across grouped sections: keep section headers,
// but cap the total number of items to `limit` (the rest reveal on scroll).
export function windowGroups(groups: SectionGroup[], limit: number): SectionGroup[] {
  let budget = Math.max(0, Number(limit) || 0)
  const out: SectionGroup[] = []
  for (const g of groups || []) {
    if (budget <= 0) break
    const items = (g.items || []).slice(0, budget)
    budget -= items.length
    out.push({ label: g.label, items })
  }
  return out
}

// Poll/checklist rendering for the reader pane. Mirrors the detail view in
// town-hall.vue: `post.options` is a string[] of choice labels, `post.members`
// a number[] of vote counts index-aligned to it. `pct` is each option's share
// of the total; `width` is the bar length relative to the winning option.
const POLL_COLORS = ['#00BCD4', '#5F32A7', '#66BB6A', '#FBC02D']

export interface PollOption { label: string; count: number; pct: number; width: number; color: string }
export interface PollView { totalVotes: number; options: PollOption[] }
export function pollView(post: any): PollView {
  const labels = post && post.options
  if (!Array.isArray(labels)) return { totalVotes: 0, options: [] }
  const members = (post && Array.isArray(post.members) ? post.members : []) as any[]
  const counts = labels.map((_, i) => Number(members[i]) || 0)
  const totalVotes = counts.reduce((sum, c) => sum + c, 0)
  const max = counts.length ? Math.max(...counts) : 0
  const options = labels.map((label, i) => {
    const count = counts[i]
    return {
      label: String(label == null ? '' : label),
      count,
      pct: totalVotes > 0 ? Math.round((count * 100) / totalVotes) : 0,
      width: max > 0 ? Math.round((count * 100) / max) : 0,
      color: POLL_COLORS[i % 4],
    }
  })
  return { totalVotes, options }
}

// Read path for checklist `options` goes through the canonical normalizer in
// store/checklist-item-helpers.ts (single source of truth for the evolving
// shape). We only drop blank-label items for display.
export function checklistView(post: any): ChecklistItem[] {
  return normalizeItems(post && post.options).filter((it) => it.label.trim().length > 0)
}

// Per-post-type glyph used for the inbox list-row leading chip, so admins can
// scan a list by post type (topic / poll / checklist). `icon` values are
// Element UI font-icon classes available globally in the app.
export interface PostTypeMeta { key: 'topic' | 'poll' | 'checklist'; icon: string; label: string; color: string }
export function postTypeMeta(post: any): PostTypeMeta {
  const t = post && post.type
  if (t === 'poll') return { key: 'poll', icon: 'el-icon-data-line', label: 'Poll', color: '#00a3b4' }
  if (t === 'checklist') return { key: 'checklist', icon: 'el-icon-finished', label: 'Checklist', color: '#3ac47d' }
  return { key: 'topic', icon: 'el-icon-chat-line-square', label: 'Topic', color: '#6c30cc' }
}

// Reader-pane pin/unpin control state. The label/icon depend only on the
// post's current pin state (`isPinned` tolerates the legacy `pinned` alias on
// read); the store always writes the canonical `pined` field. `el-icon-star-*`
// are Element UI font-icons available globally — filled when pinned.
export interface PinControl { pinned: boolean; label: 'Pin' | 'Unpin'; icon: string }
export function pinControl(post: any): PinControl {
  const pinned = isPinned(post)
  return {
    pinned,
    label: pinned ? 'Unpin' : 'Pin',
    icon: pinned ? 'el-icon-star-on' : 'el-icon-star-off',
  }
}

// Gate for the inbox comment/reply composer: a comment is submittable only
// when it carries non-whitespace text. Used to disable the Send button and to
// short-circuit the submit methods.
export function canSubmitComment(message: any): boolean {
  return typeof message === 'string' && message.trim().length > 0
}

export interface TabCounts { pinned: number; scheduled: number; drafts: number }
export function tabCounts(posts: any[]): TabCounts {
  let pinned = 0, scheduled = 0, drafts = 0
  for (const p of posts || []) {
    const st = postStatus(p)
    if (st === 'draft') { drafts++; continue }
    if (st === 'scheduled') { scheduled++; continue }
    if (p.pined === true || p.pinned === true) pinned++
  }
  return { pinned, scheduled, drafts }
}

// Per-device persistence for the "Inbox view (beta)" opt-in. The storage I/O is
// isolated here (and takes the storage object) so it stays unit-testable and so a
// disabled/private-mode localStorage degrades to "off" instead of throwing.
export const INBOX_PREF_KEY = 'townhall_inbox_beta'
export function loadInboxPref(storage: any): boolean {
  try {
    return !!storage && storage.getItem(INBOX_PREF_KEY) === 'true'
  } catch (e) {
    return false
  }
}
export function saveInboxPref(storage: any, val: boolean): void {
  try {
    if (storage) storage.setItem(INBOX_PREF_KEY, val ? 'true' : 'false')
  } catch (e) {
    // storage unavailable (private mode, quota, disabled) — non-fatal
  }
}
